<template>
  <div>
    <Head :title="is_new ? 'Nowy specjalista' : `${form.specialist.first_name} ${form.specialist.last_name}`" />
    <h1 class="mb-8 flex flex-wrap items-center gap-x-3 gap-y-2 text-3xl font-bold">
      <Link
        class="text-indigo-500 hover:text-indigo-800"
        :href="specialistsRoute"
      >
        Specjaliści
      </Link>
      <span class="font-medium text-indigo-400">/</span>
      <span v-if="is_new">Nowy specjalista</span>
      <span v-else>{{ form.specialist.first_name }} {{ form.specialist.last_name }}</span>
      <medfile-identifier-badge
        v-if="specialist.remote_uuid"
        label="remote_uuid"
        :value="specialist.remote_uuid"
      />
    </h1>
    <div class="w-full">
      <specialist-form
        v-model="form"
        v-model:editing-locale="editingLocale"
        :photo-url="specialist.photo_url"
        :available-specialisations="available_specialisations"
        :available-languages="available_languages"
        :manual="isManual"
        :show-locale-switcher="false"
        :show-active-toggle="false"
        :lock-synced-fields="Boolean(specialist.remote_uuid)"
        @submit="submit"
      >
        <footer
          class="fixed bottom-0 left-0 right-0 z-30 border-t border-gray-100 bg-white/85 shadow-[0_-8px_30px_rgba(0,0,0,0.04)] backdrop-blur-md lg:left-64"
        >
          <div class="flex flex-wrap items-center gap-x-5 gap-y-3 px-4 py-3 sm:px-6 lg:px-8">
            <record-update-dates
              compact
              :synced-at="specialist.synced_at"
              :updated-at="specialist.updated_at"
            />
            <div class="hidden h-8 w-px shrink-0 bg-gray-200 sm:block" />
            <locale-editor-switch v-model="editingLocale" />
            <div class="hidden h-8 w-px shrink-0 bg-gray-200 sm:block" />
            <div class="flex shrink-0 items-center gap-2.5">
              <span class="text-sm font-medium text-gray-700">Aktywny</span>
              <toggle-switch
                v-model="form.specialist.active"
                :errors="form.errors['specialist.active'] || []"
              />
            </div>
            <template v-if="!isManual">
              <div class="hidden h-8 w-px shrink-0 bg-gray-200 sm:block" />
              <div class="flex shrink-0 items-center gap-2.5">
                <span class="text-sm font-medium text-gray-700">Wymuś przedpłatę</span>
                <toggle-switch
                  v-model="form.specialist.requires_prepayment"
                  :errors="form.errors['specialist.requires_prepayment'] || []"
                />
              </div>
            </template>
            <div class="ml-auto flex shrink-0 items-center gap-2">
              <button
                v-if="!is_new && !specialist.remote_uuid"
                class="inline-flex h-9 min-w-24 items-center justify-center gap-1.5 rounded-lg bg-red-50 px-4 text-sm font-medium text-red-700 transition-colors hover:bg-red-100"
                type="button"
                title="Usuń"
                @click="destroy"
              >
                <icon name="trash" class="h-4 w-4 stroke-current" />
                Usuń
              </button>
              <loading-button
                :loading="form.processing"
                class="inline-flex h-9 min-w-24 items-center justify-center rounded-lg bg-indigo-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
                type="submit"
              >
                {{ is_new ? 'Utwórz' : 'Zapisz' }}
              </loading-button>
            </div>
          </div>
        </footer>
      </specialist-form>

      <specialist-slots-panel
        v-if="!is_new && isManual"
        class="mt-8"
        :specialist-id="specialist.id"
      />

      <version-history
        v-if="!is_new"
        class="mt-8 pb-28"
        :versions="versions"
      />
    </div>
  </div>
</template>

<script>
import { createLayoutHelper } from '@/utils/layoutHelper';

export default {
  layout: createLayoutHelper,
};
</script>

<script setup>
import { computed, getCurrentInstance, ref } from 'vue';
import { Head, Link, router, useForm } from '@inertiajs/vue3';
import Icon from '@/Shared/Icon.vue';
import MedfileIdentifierBadge from '@/Shared/MedfileIdentifierBadge.vue';
import RecordUpdateDates from '@/Shared/RecordUpdateDates.vue';
import LocaleEditorSwitch from '@/Shared/LocaleEditorSwitch.vue';
import ToggleSwitch from '@/Shared/ToggleSwitch.vue';
import LoadingButton from '@/Shared/LoadingButton.vue';
import VersionHistory from '@/Shared/VersionHistory.vue';
import SpecialistForm from './Form.vue';
import SpecialistSlotsPanel from './SlotsPanel.vue';
import omit from 'lodash/omit';
import { createSpecialistRouteHelper } from '@/utils/createSpecialistRouteHelper';

const { proxy } = getCurrentInstance();
const routes = proxy.$routes;
const editingLocale = ref('pl');

const props = defineProps({
  specialist: {
    type: Object,
    required: true,
  },
  // eslint-disable-next-line vue/prop-name-casing
  available_specialisations: {
    type: Array,
    default: () => [],
  },
  // eslint-disable-next-line vue/prop-name-casing
  available_languages: {
    type: Array,
    default: () => [],
  },
  // eslint-disable-next-line vue/prop-name-casing
  is_new: {
    type: Boolean,
    default: false,
  },
  versions: {
    type: Array,
    default: () => [],
  },
});

const isManual = computed(() => Boolean(props.specialist.manual));

const specialistData = omit(
  props.specialist,
  'id',
  'photo_url',
  'photo_filename',
  'full_name',
  'remote_uuid',
  'manual',
  'data_pl',
  'data_en',
);

const emptyLocale = () => ({
  description: '',
  important_information: [],
  treated_diseases: [],
  faq: [],
});

const form = useForm({
  specialist: {
    ...specialistData,
    // Non-localized arrays
    media: specialistData.media || [],
    specialisation_ids: specialistData.specialisation_ids || [],
    language_ids: specialistData.language_ids || [],
    // Localized content (PL / EN)
    data_pl: { ...emptyLocale(), ...(props.specialist.data_pl || {}) },
    data_en: { ...emptyLocale(), ...(props.specialist.data_en || {}) },
    photo: null,
  },
});

const specialistRoutes = computed(() => {
  return createSpecialistRouteHelper(null, routes);
});

const specialistsRoute = computed(() => {
  return specialistRoutes.value.index();
});

const submit = () => {
  // Only include photo in the submission if a new photo was selected; the
  // controller normalizes localized arrays / media for both locales.
  form.transform((data) => {
    const transformed = { ...data };

    if (transformed.specialist.photo === null) {
      delete transformed.specialist.photo;
    }

    return transformed;
  });

  if (props.is_new) {
    form.post(specialistRoutes.value.index());
  } else {
    form.put(specialistRoutes.value.update(props.specialist.id));
  }
};

const destroy = () => {
  const message = 'Czy na pewno chcesz usunąć tego manualnego specjalistę?\n\n'
    + 'Usunięte zostaną jego zaplanowane sloty i powiązania z usługami '
    + '(same usługi zostają). Historia wizyt pozostanie, ale bez przypisanego specjalisty.';
  if (confirm(message)) {
    router.delete(specialistRoutes.value.destroy(props.specialist.id));
  }
};
</script>
